/**
 * Configuration store using Zustand.
 */

import { create } from 'zustand';

import apiClient from '@/api/client';
import type { Project, FileType, Mountain, RpaProjectOption } from '@/types';

interface ConfigState {
  projects: Project[];
  projectsAll: Project[];
  fileTypes: FileType[];
  mountains: Mountain[];
  bvpMountainNames: string[];
  rpaProjectOptions: RpaProjectOption[];
  isLoading: boolean;
  projectsLoading: boolean;
  error: string | null;

  // Actions
  /** 默认复用已加载的项目列表并合并在途请求；变更后刷新请传 force=true */
  fetchProjects: (force?: boolean) => Promise<void>;
  fetchProjectsAll: () => Promise<void>;
  fetchFileTypes: () => Promise<void>;
  fetchMountains: () => Promise<void>;
  fetchBvpMountainNames: () => Promise<void>;
  fetchRpaProjectOptions: () => Promise<void>;
  clearError: () => void;
}

/** 在途的 /projects/ 请求，用于合并多个页面在同一时刻的重复调用 */
let projectsInFlight: Promise<void> | null = null;

export const useConfigStore = create<ConfigState>()((set, get) => ({
  projects: [],
  projectsAll: [],
  fileTypes: [],
  mountains: [],
  bvpMountainNames: [],
  rpaProjectOptions: [],
  isLoading: false,
  projectsLoading: false,
  error: null,

  fetchProjects: async (force = false) => {
    if (!force) {
      if (projectsInFlight) return projectsInFlight;
      if (get().projects.length > 0) return;
    }

    const request = (async () => {
      set({ projectsLoading: true, error: null });
      try {
        const response = await apiClient.get<{ results: Project[] }>('/projects/');
        set({ projects: response.data.results || response.data, projectsLoading: false });
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          '获取项目列表失败';
        set({ error: message, projectsLoading: false });
      } finally {
        projectsInFlight = null;
      }
    })();

    projectsInFlight = request;
    return request;
  },

  fetchProjectsAll: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get<{ results: Project[] }>('/projects/', {
        params: { include_mainline: 'true' },
      });
      set({ projectsAll: response.data.results || response.data, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取项目列表失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchFileTypes: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get<{ results: FileType[] }>('/file-types/');
      set({ fileTypes: response.data.results || response.data, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取文件类型失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchMountains: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get<Mountain[]>('/mountains/');
      const data = response.data;
      set({ mountains: Array.isArray(data) ? data : (data as { results: Mountain[] }).results ?? [], isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取山头列表失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchBvpMountainNames: async () => {
    try {
      const response = await apiClient.get<{ names: string[] }>('/mountains/bvp-names/');
      set({ bvpMountainNames: response.data.names ?? [] });
    } catch {
      // BVP 不可用时静默忽略，不影响 BSP 山头管理页面的其他功能
      set({ bvpMountainNames: [] });
    }
  },

  fetchRpaProjectOptions: async () => {
    try {
      const response = await apiClient.get<{ options: RpaProjectOption[] }>(
        '/mountains/rpa-project-options/',
      );
      set({ rpaProjectOptions: response.data.options ?? [] });
    } catch {
      // RPA 不可用时静默降级为空候选，输入框仍可手动填写
      set({ rpaProjectOptions: [] });
    }
  },

  clearError: () => set({ error: null }),
}));
